import { type AuthModeOrgSettings, type AuthModeUser, normalizeAuthModeUser } from '@/lib/authModeApi';
import { PENDING_SHARED_TEMPLATE_STORAGE_KEY } from '@/lib/pendingSharedTemplate';
import { safeNextPath } from '@/lib/safeNextPath';
import { clearSentryUser, setSentryUser } from '@/lib/sentry';
import { useMemberships } from '@careflux/auth';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

const BETA_ROLLOUT_PERMISSION = 'features:beta';
const SAMEIND_ORDERING_PERMISSION = 'features:sameind';
// Exported so route guards (App.tsx) can gate on the clinic-admin permission set
// without hard-coding permission strings at the call site.
export const ACCOUNTS_ADMIN_PERMISSION = 'accounts:admin';
const ACCOUNTS_IMPERSONATE_PERMISSION = 'accounts:impersonate';
// Mirrors the BFF's require_scribe_permission(SESSIONS_READ) on /api/memory.
const SESSIONS_READ_PERMISSION = 'sessions:read';
// The scribe clinic-admin permission (auth issues `org:admin`; `accounts:admin`
// subsumes it). Exported for route guards.
export const ORG_ADMIN_PERMISSION = 'org:admin';
const SESSIONS_WRITE_PERMISSION = 'sessions:write';
const TEMPLATES_WRITE_PERMISSION = 'templates:write';
const AUTH_LOGIN_URL = '/api/auth/login';

// Persisted alongside other sessionStorage entries so the cross-user wipe
// check survives the logout → /login navigation that resets React state.
const ACTIVE_USER_ID_STORAGE_KEY = 'careflux_active_user_id';

export interface OriginalUser {
  name: string;
}

// A delegation granted TO the current user (an account they may view as). Auth's
// DelegationResponse is keyed on account UUIDs and carries no display name, so the
// "viewing as" UI identifies the delegator by account id.
export interface DelegatedAccount {
  id: string;
  delegator_account_id: string;
  permissions: string[];
  is_active: boolean;
  activated_at: string | null;
}

export interface User {
  id: string;
  authAccountId?: string;
  authOrgId?: string;
  identityId?: string;
  permissions?: string[];
  disciplines?: string[];
  // Auth labels (profession, specialty), e.g. 'social_worker'
  labels?: string[];
  orgSettings?: AuthModeOrgSettings;
  email: string;
  name: string;
  title?: string;
  initials?: string;
  // Impersonation fields
  impersonating?: boolean;
  originalUser?: OriginalUser;
  hasLaeknanumer?: boolean;
  hasSameindStofnun?: boolean;
}

/** Check if a user has access to the Verkefnalisti based on auth-org rollout settings. */
export const hasVerkefniAccess = (user: User | null): boolean => {
  const rollout = user?.orgSettings?.verkefniRollout ?? 'all';
  return rollout !== 'disabled' && (rollout !== 'beta' || (user?.permissions ?? []).includes(BETA_ROLLOUT_PERMISSION));
};

export const hasSystemAdminAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(ACCOUNTS_ADMIN_PERMISSION);

export const hasClinicAdminAccess = (user: User | null): boolean => {
  // System admins (accounts:admin) subsume clinic admins; org:admin grants
  // clinic-scoped access. Authz is permission-based only — no roles.
  const permissions = user?.permissions ?? [];
  return permissions.includes(ORG_ADMIN_PERMISSION) || permissions.includes(ACCOUNTS_ADMIN_PERMISSION);
};

/** Mirrors the BFF gate on writes such as settings updates and onboarding completion. */
export const hasSessionsWriteAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(SESSIONS_WRITE_PERMISSION);

/** Mirrors the BFF gate on template writes (create, edit, example note). */
export const hasTemplatesWriteAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(TEMPLATES_WRITE_PERMISSION);

export const hasImpersonationAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(ACCOUNTS_IMPERSONATE_PERMISSION);

/**
 * Whether the user is a beta tester, via the auth-mode `features:beta`
 * permission — the same beta signal `hasVerkefniAccess` keys on.
 *
 * Use this for any beta-only feature gate. Beta access is permission-based; the
 * BFF payload carries `permissions` but no legacy role list.
 */
export const hasBetaAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(BETA_ROLLOUT_PERMISSION);

/** Sameind blood-test ordering is rolled out per org via the `features:sameind` flag. */
export const hasSameindAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(SAMEIND_ORDERING_PERMISSION);

/**
 * Whether the user can open Minni (/settings/memory) — the memory routes 403
 * without `sessions:read` (e.g. delegate-only accounts with no org of their own).
 */
export const hasMemoryAccess = (user: User | null): boolean =>
  (user?.permissions ?? []).includes(SESSIONS_READ_PERMISSION);

/** Summary verification (live "Yfirferð") is beta-only. */
export const hasSummaryVerificationAccess = (user: User | null): boolean => hasBetaAccess(user);

interface AuthContextType {
  user: User | null;
  authAccountId: string | null;
  authOrgId: string | null;
  identityId: string | null;
  permissions: string[];
  disciplines: string[];
  authOrgName: string | null;
  canSwitchOrg: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  originalUser: OriginalUser | null;
  // Delegate access (auth-native: keyed on the delegator's auth_account_id)
  viewingAsAuthAccountId: string | null;
  viewingAsAccount: DelegatedAccount | null;
  delegatedAccounts: DelegatedAccount[];
  isViewingAsDelegate: boolean;
  isDelegateOnly: boolean;
  setViewingAsAuthAccountId: (authAccountId: string | null) => void;
  fetchDelegatedAccounts: () => Promise<void>;
  // Actions
  login: (inviteToken?: string, nextPath?: string | null) => void;
  logout: () => void;
  refetch: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  startImpersonation: (targetAuthAccountId: string) => Promise<boolean>;
  stopImpersonation: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
type LoginRedirectTarget = Pick<Location, 'href'>;
interface AuthProviderProps {
  children: ReactNode;
  loginRedirectTarget?: LoginRedirectTarget;
}

const toContextUser = (authUser: AuthModeUser): User => {
  const authContextUser = authUser as AuthModeUser & Partial<User>;

  return {
    ...authContextUser,
    id: authContextUser.id ?? authUser.identityId,
    email: authContextUser.email ?? '',
    name: authContextUser.name ?? '',
    authAccountId: authUser.authAccountId,
    authOrgId: authUser.authOrgId,
    identityId: authUser.identityId,
    permissions: authUser.permissions,
    disciplines: authUser.disciplines,
    labels: authUser.labels,
    orgSettings: authUser.orgSettings,
  };
};

export const AuthProvider = ({ children, loginRedirectTarget = window.location }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const memberships = useMemberships({ enabled: !!user });

  // Delegate access state
  const [viewingAsAuthAccountId, setViewingAsAuthAccountIdState] = useState<string | null>(null);
  const [delegatedAccounts, setDelegatedAccounts] = useState<DelegatedAccount[]>([]);

  const fetchUser = async () => {
    try {
      const response = await fetch('/api/auth/user', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        const authUser = normalizeAuthModeUser(userData);
        const contextUser = toContextUser(authUser);
        if (user && user.authAccountId !== contextUser.authAccountId) {
          queryClient.clear();
          setDelegatedAccounts([]);
          setViewingAsAuthAccountId(null);
        }
        setUser(contextUser);
        // Both are optional — we do not always have them for a clinician.
        setSentryUser(authUser.authAccountId, { email: contextUser.email, name: contextUser.name });
      } else {
        setUser(null);
        clearSentryUser();
      }
    } catch (error) {
      if (!(error instanceof Error && error.message === 'not authenticated')) {
        console.error('Failed to fetch user:', error);
      }
      setUser(null);
      clearSentryUser();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDelegatedAccounts = useCallback(async () => {
    try {
      const response = await fetch('/api/delegated-access', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setDelegatedAccounts(data.delegations || []);
      } else {
        setDelegatedAccounts([]);
      }
    } catch (error) {
      console.error('Failed to fetch delegated accounts:', error);
      setDelegatedAccounts([]);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetch on mount only
  useEffect(() => {
    fetchUser();
  }, []);

  // Fetch delegated accounts after user is loaded
  useEffect(() => {
    if (user?.authAccountId) {
      fetchDelegatedAccounts();
    }
  }, [user?.authAccountId, fetchDelegatedAccounts]);

  // Loading or failed membership reads must never trigger delegate-only mode.
  const isDelegateOnly =
    !!user && memberships.isSuccess && memberships.data.length === 0 && delegatedAccounts.length > 0;

  // Auto-select the first delegation only after confirming no own membership.
  useEffect(() => {
    if (
      isDelegateOnly &&
      delegatedAccounts.length > 0 &&
      viewingAsAuthAccountId === null &&
      !sessionStorage.getItem('viewingAsAuthAccountId')
    ) {
      setViewingAsAuthAccountIdState(delegatedAccounts[0].delegator_account_id);
      sessionStorage.setItem('viewingAsAuthAccountId', delegatedAccounts[0].delegator_account_id);
    }
  }, [isDelegateOnly, delegatedAccounts, viewingAsAuthAccountId]);

  const setViewingAsAuthAccountId = useCallback((authAccountId: string | null) => {
    setViewingAsAuthAccountIdState(authAccountId);
    // Store in sessionStorage so it persists across page refreshes
    if (authAccountId !== null) {
      sessionStorage.setItem('viewingAsAuthAccountId', authAccountId);
    } else {
      sessionStorage.removeItem('viewingAsAuthAccountId');
    }
  }, []);

  // Restore viewingAsAuthAccountId from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('viewingAsAuthAccountId');
    if (stored) {
      setViewingAsAuthAccountIdState(stored);
    }
  }, []);

  // Cross-user safety: wipe sessionStorage whenever the authenticated user id
  // differs from the previously-seen owner of this tab. Shared-workstation
  // usage is supported, and /api/auth/logout does not clear sessionStorage
  // — without this, User A's notes survive a User B login on the same tab.
  // Impersonation flips `user.id` to the impersonated identity; treat that
  // as same-owner so legitimate impersonation does not wipe context.
  useEffect(() => {
    if (!user?.id || user.impersonating) return;
    const previous = sessionStorage.getItem(ACTIVE_USER_ID_STORAGE_KEY);
    if (previous && previous !== user.id) {
      // A share link the new user just opened is theirs, not the previous user's: keep only that key.
      const pendingShare = sessionStorage.getItem(PENDING_SHARED_TEMPLATE_STORAGE_KEY);
      sessionStorage.clear();
      if (pendingShare) sessionStorage.setItem(PENDING_SHARED_TEMPLATE_STORAGE_KEY, pendingShare);
      // sessionStorage.clear() removes the persisted viewingAsAuthAccountId, but the
      // restore-on-mount effect (above) already hydrated it into React state.
      // Without explicitly resetting here, User B inherits User A's delegator
      // account id and the first POST /sessions/.../user-summary fires with
      // ?viewing_as_auth_account_id=<A's delegator> — a cross-user data leak.
      setViewingAsAuthAccountIdState(null);
      setDelegatedAccounts([]);
    }
    sessionStorage.setItem(ACTIVE_USER_ID_STORAGE_KEY, user.id);
  }, [user?.id, user?.impersonating]);

  const login = useCallback(
    (inviteToken?: string, nextPath?: string | null) => {
      const params = new URLSearchParams();
      const token = inviteToken?.trim() ?? '';
      if (token) params.set('invite_token', token);
      const next = safeNextPath(nextPath);
      if (next) params.set('next', next);
      const query = params.toString();
      loginRedirectTarget.href = query ? `${AUTH_LOGIN_URL}?${query}` : AUTH_LOGIN_URL;
    },
    [loginRedirectTarget]
  );

  const logout = async () => {
    try {
      clearSentryUser();
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const refetch = async () => {
    await fetchUser();
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  const startImpersonation = async (targetAuthAccountId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/admin/impersonate/${targetAuthAccountId}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        // Refetch user to get updated session data
        await fetchUser();
        return true;
      }
      const error = await response.json();
      console.error('Failed to start impersonation:', error);
      return false;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return false;
    }
  };

  const stopImpersonation = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/admin/stop-impersonation', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        // Refetch user to get restored session data
        await fetchUser();
        return true;
      }
      const error = await response.json();
      console.error('Failed to stop impersonation:', error);
      return false;
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      return false;
    }
  };

  // Derived impersonation state
  const isImpersonating = user?.impersonating ?? false;
  const originalUser = user?.originalUser ?? null;
  const authAccountId = user?.authAccountId ?? null;
  const authOrgId = user?.authOrgId ?? null;
  const identityId = user?.identityId ?? null;
  const permissions = user?.permissions ?? [];
  const disciplines = user?.disciplines ?? [];
  const authOrgName = memberships.data?.find((membership) => membership.account_id === authAccountId)?.org_name ?? null;
  const canSwitchOrg = (memberships.data?.length ?? 0) > 1;

  // Derived delegate state
  const isViewingAsDelegate = viewingAsAuthAccountId !== null;
  const viewingAsAccount = delegatedAccounts.find((d) => d.delegator_account_id === viewingAsAuthAccountId) ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        authAccountId,
        authOrgId,
        identityId,
        permissions,
        disciplines,
        authOrgName,
        canSwitchOrg,
        isLoading,
        isAuthenticated: !!user,
        isImpersonating,
        originalUser,
        // Delegate access
        viewingAsAuthAccountId,
        viewingAsAccount,
        delegatedAccounts,
        isViewingAsDelegate,
        isDelegateOnly,
        setViewingAsAuthAccountId,
        fetchDelegatedAccounts,
        // Actions
        login,
        logout,
        refetch,
        updateUser,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Auth context, or undefined outside an AuthProvider (e.g. the tokenless embed). Never throws. */
export const useOptionalAuth = () => useContext(AuthContext);
